import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Listbox } from "@headlessui/react";
import { createPortal } from "react-dom";
import {
  CheckIcon,
  ExclamationCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import ReactTags from "react-tag-autocomplete";

import { Label, NonBreakingSpace } from "@helloaudio/ui";
import { cn } from "@helloaudio/utils";

interface SelectProps<T> {
  className?: string;
  label?: string;
  labelClassName?: string;
  error?: string;
  name?: string;
  placeholder?: string;
  value: T;
  disabled?: boolean;
  checkIcon?: boolean;
  onBlur?: (value: T) => void;
  onChange: (value: T) => void;
  options: { key?: string; label: string; value: T; disabled?: boolean }[];
  optionsClassName?: string;
  optionItemClassName?: string;
  portal?: boolean;
  portalTarget?: string | HTMLElement;
}

function Select<T>(props: SelectProps<T>) {
  const {
    className,
    value,
    onChange,
    label,
    labelClassName,
    error,
    disabled,
    options,
    optionsClassName,
    optionItemClassName,
    placeholder,
    checkIcon,
    portal,
    portalTarget,
  } = props;

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const resolvedPortalTarget = useMemo(() => {
    if (!portal || typeof document === "undefined") {
      return null;
    }

    if (typeof portalTarget === "string") {
      try {
        const element = document.querySelector(portalTarget);
        if (element instanceof HTMLElement) {
          return element;
        }
      } catch {
        return document.body;
      }
    }

    if (typeof HTMLElement !== "undefined" && portalTarget instanceof HTMLElement) {
      return portalTarget;
    }

    return document.body;
  }, [portal, portalTarget]);

  const updateMenuPosition = useCallback(() => {
    if (!portal || !resolvedPortalTarget || !buttonRef.current) {
      return;
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();

    if (resolvedPortalTarget === document.body) {
      setMenuStyle({
        position: "fixed",
        top: buttonRect.bottom + 4,
        left: buttonRect.left,
        width: buttonRect.width,
      });
    } else {
      const targetRect = resolvedPortalTarget.getBoundingClientRect();
      setMenuStyle({
        position: "absolute",
        top: buttonRect.bottom - targetRect.top + resolvedPortalTarget.scrollTop + 4,
        left: buttonRect.left - targetRect.left + resolvedPortalTarget.scrollLeft,
        width: buttonRect.width,
      });
    }
  }, [portal, resolvedPortalTarget]);

  useLayoutEffect(() => {
    if (!portal || !resolvedPortalTarget) {
      return undefined;
    }

    let frame = 0;
    const scheduleUpdate = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          updateMenuPosition();
        });
      }
    };

    scheduleUpdate();

    window.addEventListener("resize", scheduleUpdate, { passive: true });
    if (resolvedPortalTarget === document.body) {
      window.addEventListener("scroll", scheduleUpdate, { passive: true, capture: true });
    }

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("resize", scheduleUpdate);
      if (resolvedPortalTarget === document.body) {
        window.removeEventListener("scroll", scheduleUpdate, true);
      }
    };
  }, [portal, resolvedPortalTarget, updateMenuPosition]);

  return (
    <Listbox as="div" disabled={disabled} className={className} value={value} onChange={onChange}>
      {({ open, disabled }) => {
        const optionsNode = (
          <Listbox.Options
            style={portal ? menuStyle : undefined}
            className={cn(
              "tw-list-none tw-m-0 tw-p-0 tw-z-dropdown tw-bg-white tw-border-solid tw-border tw-rounded tw-border-gray-300 tw-max-h-60 tw-py-1 tw-text-sm tw-shadow-lg tw-overflow-auto focus:tw-outline-none sm:tw-text-base",
              { "tw-absolute tw-mt-1 tw-w-full": !portal },
              optionsClassName,
            )}
          >
            {options.map((option, index) => (
              <Listbox.Option
                key={option.key !== undefined ? option.key : index}
                className={({ active }) =>
                  cn(
                    value === option.value && "tw-bg-yellow-100",
                    active && "tw-bg-yellow-100",
                    checkIcon && "tw-pl-8",
                    "tw-cursor-pointer tw-text-gray-900 tw-select-none tw-relative tw-py-2 tw-pl-4 tw-pr-4",
                  )
                }
                value={option.value}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={cn(
                        selected ? "tw-font-semibold" : "tw-font-normal",
                        "tw-block tw-truncate tw-font-Montserrat",
                      )}
                    >
                      {option.label}
                    </span>

                    {selected && checkIcon && (
                      <span
                        className={cn(
                          "tw-text-indigo-800 tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-1.5",
                          optionItemClassName,
                        )}
                      >
                        <CheckIcon className="tw-h-5 tw-w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        );

        return (
          <>
            {label && (
              <Listbox.Label as={Label} className={cn("tw-mb-2", labelClassName)}>
                {label}
              </Listbox.Label>
            )}

            <div className="tw-relative">
              <Listbox.Button
                className={cn(
                  "tw-transition-colors tw-duration-100 tw-relative tw-w-full tw-border-solid tw-border tw-rounded tw-pl-3 tw-pr-10 tw-py-2 tw-text-left tw-text-sm tw-cursor-default focus:tw-outline-none focus:tw-ring-0 sm:tw-text-base",
                  !error && "focus:tw-border-indigo-800 focus:tw-bg-indigo-10",
                  error && "focus:tw-border-red-500 focus:tw-bg-red-10",
                  !error && (open ? "tw-border-indigo-800" : "tw-border-gray-300"),
                  error && (open ? "tw-border-red-500" : "tw-border-red-300"),
                  open && !disabled && error && "tw-bg-red-10",
                  open && !disabled && !error && "tw-bg-indigo-10",
                  !open && !disabled && "tw-bg-white",
                  disabled && "tw-bg-gray-100",
                  optionItemClassName,
                )}
                onClick={portal ? updateMenuPosition : undefined}
                onFocus={portal ? updateMenuPosition : undefined}
                ref={buttonRef}
              >
                <span
                  className={cn(
                    "tw-block tw-truncate tw-font-Montserrat",
                    value === undefined || value === "" ? "tw-text-gray-400" : "tw-text-gray-900",
                  )}
                >
                  {(value === undefined || value === ""
                    ? placeholder
                    : options.find((option) => option.value === value)?.label) || (
                    <NonBreakingSpace />
                  )}
                </span>
                <span className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-2 tw-pointer-events-none">
                  {open ? (
                    <ChevronUpIcon className="tw-shrink-0 tw-w-5 tw-h-5 feed-arrowIcon" />
                  ) : (
                    <ChevronDownIcon className="tw-shrink-0 tw-w-5 tw-h-5 feed-arrowIcon" />
                  )}
                </span>
              </Listbox.Button>

              {error && (
                <div className="tw-absolute tw-inset-y-0 tw-right-5 tw-pr-3 tw-flex tw-items-center tw-pointer-events-none">
                  <ExclamationCircleIcon
                    className="tw-h-5 tw-w-5 tw-text-red-500"
                    aria-hidden="true"
                  />
                </div>
              )}

              {portal && resolvedPortalTarget
                ? createPortal(optionsNode, resolvedPortalTarget)
                : optionsNode}
            </div>
            {error && (
              <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-italic tw-text-red-600">{error}</p>
            )}
          </>
        );
      }}
    </Listbox>
  );
}

export default Select;

interface SelectTag {
  id: string | number;
  name: string;
}

interface MultiSelectProps {
  className?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  labelClassName?: string;
  onAdd: (tag: SelectTag) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
  suggestions: SelectTag[];
  tags: SelectTag[];
}

export function MultiSelect(props: MultiSelectProps) {
  const {
    className,
    disabled,
    error,
    label,
    labelClassName,
    onAdd,
    onRemove,
    placeholder,
    suggestions,
    tags,
  } = props;

  const availableSuggestions = useMemo(
    () => suggestions.filter((option) => !tags.some((tag) => tag.id === option.id)),
    [suggestions, tags],
  );

  return (
    <div className={className}>
      {label && <Label className={cn("tw-mb-2", labelClassName)}>{label}</Label>}

      <div
        className={cn(
          "tw-transition-colors tw-duration-100 tw-relative tw-w-full tw-border-solid tw-border tw-rounded tw-pl-3 tw-pr-3 tw-py-2 tw-text-left tw-cursor-text focus:tw-outline-none focus:tw-ring-0 sm:tw-text-sm tw-bg-white",
          !error &&
            "focus-within:tw-border-indigo-800 focus-within:tw-bg-indigo-10 tw-border-gray-300",
          error && "focus-within:tw-border-red-500 focus-within:tw-bg-red-10 tw-border-red-300",
          disabled && "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed",
        )}
      >
        <ReactTags
          addOnBlur
          allowNew={false}
          autoresize
          // @ts-expect-error this merges the classes with the defaults but TS doesn't think it does
          classNames={{
            root: cn("react-tags", "tw-py-0 tw-px-1"),
          }}
          noSuggestionsText="No options"
          onAddition={onAdd}
          onDelete={onRemove}
          placeholderText={placeholder}
          suggestions={availableSuggestions}
          tags={tags}
        />
      </div>

      {error && <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-italic tw-text-red-600">{error}</p>}
    </div>
  );
}
